import { defineStore } from 'pinia';
import { useHeadGenerate } from '~/composables/useHeadGenerate';
import { createPopulateString } from '~/utils/strapiComponents/addStrapiData';
import { useStoreRoot } from '../abstract/root';
import { softswiss } from '../transport/softswiss';
import { strapi } from '../transport/strapi';
import type { PagesLoadOptions } from '../abstract/pages';
import type { IBreadcrumbs } from '~/types/breadcrumbs';
import type { IGameInfo, IGameInfoStrapiResponse, IGameSpecs } from '~/types/games';
import type { ISeoHeadComponent } from '~/types/seoHead';
import type { ISsGameInfoResponse } from '~/types/softswiss/ssGameInfo';

const initialStrapiGameInfoData = {
  descriptionText: '',
  head: {} as ISeoHeadComponent,
  breadcrumbsItem: [] as IBreadcrumbs[],
  hitRate: 0,
  lines: 0,
  payout: 0,
  volatilityRating: 0,
  ways: 0,
  showHitRate: true,
  showLines: true,
  showPayout: true,
  showVolatilityRating: true,
  showWays: true,
  specs: [] as IGameSpecs[],
  isGameFilledInStrapi: false,
};

export const useStoreGameInfo = defineStore('gameInfo', {
  state: () => ({
    strapiGameInfoData: { ...initialStrapiGameInfoData },
    ssGameInfoData: {
      gameId: null,
      description: '',
      hit_rate: '',
      identifier: '',
      lines: NaN,
      payout: NaN,
      volatility_rating: '',
      ways: NaN,
    } as Partial<ISsGameInfoResponse>,
    requestPromiseSs: null as Promise<any> | null,
    requestPromiseStrapi: null as Promise<IGameInfoStrapiResponse> | null,
  }),

  getters: {
    gameInfo(state) {
      const gameInfoData = {
        identifier: state.ssGameInfoData.identifier,
        hitRate: state.ssGameInfoData.hit_rate,
        volatilityRating: state.ssGameInfoData.volatility_rating,
        lines: state.ssGameInfoData.lines,
        payout: state.ssGameInfoData.payout,
        ways: state.ssGameInfoData.ways,
      } as any;

      const { descriptionText, hitRate, lines, payout, volatilityRating, ways, ...rest } =
        state.strapiGameInfoData;

      const gameInfo = {
        descriptionText,
      } as ISsGameInfoResponse;

      if (hitRate) {
        gameInfo.hitRate = hitRate;
      }

      if (lines) {
        gameInfo.lines = lines;
      }

      if (payout) {
        gameInfo.payout = payout;
      }
      if (volatilityRating) {
        gameInfo.volatilityRating = volatilityRating;
      }

      if (ways) {
        gameInfo.ways = ways;
      }

      return {
        ...gameInfoData,
        ...rest,
        ...gameInfo,
      };
    },
  },

  actions: {
    async loadSsGameInfo(identifier: string) {
      try {
        if (!this.requestPromiseSs) {
          this.requestPromiseSs = softswiss()
            .get({ path: `/api/games_info/${identifier}` })
            .finally(() => (this.requestPromiseSs = null));
        }

        const gameInfoSsDataResponse: ISsGameInfoResponse = await this.requestPromiseSs;

        if (gameInfoSsDataResponse) {
          this.ssGameInfoData = gameInfoSsDataResponse;
        }
      } catch (e) {
        console.error(e);
      }
    },
    async loadStrapiGameInfo(options?: PagesLoadOptions) {
      if (!this.requestPromiseStrapi) {
        let filterString = '';
        Object.keys(options as Record<string, string | boolean>).forEach((key) => {
          if (key === 'gameId') {
            filterString = `filters[gameId][$eq]=${options?.gameId}`;
          } else if (key === 'gameUrl') {
            filterString = `filters[url][$eq]=${options?.gameUrl}`;
          }
        });

        const populateString = createPopulateString([
          'head',
          'head.alternates',
          'specs',
          'breadcrumbsItem',
          'breadcrumbsItem.itemImage',
          'mirrorHead',
          'mirrorBody',
        ]);

        this.requestPromiseStrapi = strapi()
          .get('game-infos', `${filterString}&${populateString}`)
          .finally(() => (this.requestPromiseStrapi = null));
      }

      const response = await this.requestPromiseStrapi;

      if (!response) {
        return false;
      }

      if (response?.data && response.data[0]) {
        this.RESET_STRAPI_GAME_INFO_STATE();
        this.ADD_DATA_FROM_STRAPI(response.data[0]);
      } else if (options?.gameId || options?.gameUrl) {
        this.RESET_STRAPI_GAME_INFO_STATE();
      }

      return true;
    },
    RESET_STRAPI_GAME_INFO_STATE() {
      this.strapiGameInfoData = { ...initialStrapiGameInfoData };
    },
    ADD_DATA_FROM_STRAPI(data: IGameInfo) {
      const geo = useStoreRoot().geo;

      if (geo && geo !== 'main') {
        const mirrorHead = data.mirrorHead?.find((head) => head?.geo === geo);
        const { getMirrorCanonical } = useHeadGenerate();

        if (mirrorHead) {
          data.descriptionText = '';
          data.head.title = mirrorHead.title;
          data.head.description = mirrorHead.description || '';
          data.head.nofollow = mirrorHead.nofollow || false;
          data.head.noindex = mirrorHead.noindex || false;
          data.head.canonical = getMirrorCanonical(mirrorHead);

          if (mirrorHead.noindex || mirrorHead.nofollow) {
            data.head.alternates = [];
          }
        }

        const mirrorBody = data.mirrorBody?.find((body) => body?.geo === geo);

        if (mirrorBody) {
          data.descriptionText = mirrorBody.descriptionText;
        }
      }

      let breadcrumbsItem = [] as IBreadcrumbs[];

      if (data.breadcrumbsItem) {
        breadcrumbsItem = data.breadcrumbsItem;
      }

      // @ts-ignore
      this.strapiGameInfoData = {
        ...data,
        breadcrumbsItem,
        isGameFilledInStrapi: true,
      };
    },
  },
});
